<template>
  <div class="animated fadeIn">
    <h4 class="mb-3"><i class="icon-docs mr-2 text-primary"></i>Downtime Reports</h4>

    <!-- Filter Bar -->
    <b-card class="mb-4">
      <b-form inline @submit.prevent="applyFilters">
        <label class="mr-2 font-weight-bold">Service</label>
        <b-form-select v-model="filters.service" :options="serviceOptions" class="mr-3 mb-2" style="width:160px" />

        <label class="mr-2 font-weight-bold">Status</label>
        <b-form-select v-model="filters.status" :options="statusOptions" class="mr-3 mb-2" style="width:130px" />

        <label class="mr-2 font-weight-bold">From</label>
        <b-form-input v-model="filters.from" type="date" class="mr-3 mb-2" style="width:150px" />

        <label class="mr-2 font-weight-bold">To</label>
        <b-form-input v-model="filters.to" type="date" class="mr-3 mb-2" style="width:150px" />

        <b-button type="submit" variant="primary" class="mb-2" :disabled="loading">
          <i class="icon-magnifier mr-1"></i>Apply
        </b-button>
        <b-button variant="outline-secondary" class="ml-2 mb-2" @click="resetFilters">Reset</b-button>
      </b-form>
    </b-card>

    <!-- Summary Cards -->
    <b-row class="mb-4">
      <b-col md="4" class="mb-3">
        <b-card class="text-center border-primary h-100">
          <div class="text-primary" style="font-size:2rem;font-weight:700">{{ summary.totalIncidents || 0 }}</div>
          <div class="text-muted small">Total Incidents</div>
        </b-card>
      </b-col>
      <b-col md="4" class="mb-3">
        <b-card class="text-center border-info h-100">
          <div class="text-info" style="font-size:2rem;font-weight:700">
            {{ summary.avgResponseMs ? Math.round(summary.avgResponseMs) + ' ms' : '—' }}
          </div>
          <div class="text-muted small">Avg Response Time</div>
        </b-card>
      </b-col>
      <b-col md="4" class="mb-3">
        <b-card class="text-center border-warning h-100">
          <div class="text-warning" style="font-size:1.4rem;font-weight:700;margin-top:.4rem">
            {{ summary.mostAffected || '—' }}
          </div>
          <div class="text-muted small">Most Affected Service</div>
        </b-card>
      </b-col>
    </b-row>

    <!-- Daily Bar Chart -->
    <b-card class="mb-4" no-body>
      <b-card-header><i class="icon-graph mr-1"></i>Incidents Per Day</b-card-header>
      <b-card-body>
        <canvas ref="barChart" height="60"></canvas>
      </b-card-body>
    </b-card>

    <!-- Events Table -->
    <b-card no-body>
      <b-card-header class="d-flex justify-content-between align-items-center">
        <span><i class="icon-list mr-1"></i>Events
          <b-badge variant="secondary" class="ml-1">{{ total }}</b-badge>
        </span>
        <b-spinner v-if="loading" small variant="primary" />
      </b-card-header>
      <b-table
        :items="events"
        :fields="fields"
        :busy="loading"
        responsive striped hover show-empty
        empty-text="No events match the current filters."
        class="mb-0"
      >
        <template #table-busy>
          <div class="text-center py-3"><b-spinner variant="primary" /> Loading…</div>
        </template>
        <template #cell(status)="d">
          <b-badge :variant="badgeVariant(d.value)">{{ d.value }}</b-badge>
        </template>
        <template #cell(entry_type)="d">
          <b-badge :variant="d.value === 'MANUAL' ? 'info' : 'light'">{{ d.value }}</b-badge>
        </template>
        <template #cell(response_time_ms)="d">
          <span v-if="d.value != null">{{ d.value }} ms</span>
          <span v-else class="text-muted">—</span>
        </template>
        <template #cell(checked_at)="d">{{ d.value | fmtdt }}</template>
      </b-table>
      <b-card-footer v-if="total > pageSize">
        <b-pagination
          v-model="currentPage"
          :total-rows="total"
          :per-page="pageSize"
          @change="onPageChange"
          align="center"
          class="mb-0"
        />
      </b-card-footer>
    </b-card>
  </div>
</template>

<script>
import Chart from 'chart.js'
import downtimeService from '@/services/downtimeService'

export default {
  name: 'DowntimeReports',
  filters: {
    fmtdt: v => v ? new Date(v).toLocaleString() : '—'
  },
  data() {
    return {
      loading: false,
      filters: { service: '', status: '', from: '', to: '' },
      summary: {},
      total: 0,
      events: [],
      currentPage: 1,
      pageSize: 20,
      barChart: null,
      serviceOptions: [
        { value: '', text: 'All Services' },
        { value: 'hrm-api', text: 'HRM API' },
        { value: 'admin-api', text: 'Admin API' },
        { value: 'billing-service', text: 'Billing Service' },
      ],
      statusOptions: [
        { value: '', text: 'All Statuses' },
        { value: 'UP', text: 'UP' },
        { value: 'DOWN', text: 'DOWN' },
        { value: 'DEGRADED', text: 'DEGRADED' },
      ],
      fields: [
        { key: 'service_name',    label: 'Service',     sortable: true },
        { key: 'status',          label: 'Status',      sortable: true },
        { key: 'response_time_ms',label: 'Response ms', sortable: true },
        { key: 'entry_type',      label: 'Type' },
        { key: 'reported_by',     label: 'Reported By' },
        { key: 'message',         label: 'Message' },
        { key: 'checked_at',      label: 'Checked At',  sortable: true },
      ]
    }
  },
  created() {
    this.fetchReports(0)
  },
  methods: {
    applyFilters() { this.currentPage = 1; this.fetchReports(0) },
    resetFilters()  { this.filters = { service: '', status: '', from: '', to: '' }; this.applyFilters() },
    onPageChange(p) { this.fetchReports(p - 1) },
    fetchReports(page) {
      this.loading = true
      downtimeService.getReports(
        Object.assign({}, this.filters, { page, size: this.pageSize }),
        data => {
          this.total   = data.total   || 0
          this.summary = data.summary || {}
          this.events  = data.events  || []
          this.loading = false
          this.$nextTick(() => this.renderBarChart(data.dailyCounts || []))
        },
        err => {
          this.loading = false
          this.$bvToast.toast(err, { title: 'Error', variant: 'danger', solid: true })
        }
      )
    },
    renderBarChart(daily) {
      const ctx = this.$refs.barChart
      if (!ctx) return
      const labels = daily.map(d => d.date)
      const counts = daily.map(d => d.count)
      if (this.barChart) {
        this.barChart.data.labels = labels
        this.barChart.data.datasets[0].data = counts
        this.barChart.update()
        return
      }
      this.barChart = new Chart(ctx, {
        type: 'bar',
        data: {
          labels,
          datasets: [{
            label: 'Incidents',
            data: counts,
            backgroundColor: 'rgba(54,162,235,.6)',
            borderColor:     'rgba(54,162,235,1)',
            borderWidth: 1
          }]
        },
        options: {
          responsive: true, maintainAspectRatio: true,
          scales: { yAxes: [{ ticks: { beginAtZero: true, stepSize: 1 } }] },
          legend: { display: false }
        }
      })
    },
    badgeVariant(s) {
      return s === 'UP' ? 'success' : s === 'DOWN' ? 'danger' : s === 'DEGRADED' ? 'warning' : 'secondary'
    }
  }
}
</script>
